/**
 * Design system tokens — colors, typography, shadows, borders, spacing.
 *
 * Palette (unified):
 *   Primary:  #57837E (teal) — brand color, focus states, accents
 *   Text:     3 levels — primary / secondary / muted
 *   BG:       white / bg / bgHover
 *   Border:   2 levels — default / hover
 *   Status:   success / error / warning / info (each: main + bg + text)
 *   Gender:   info=male, female, neutral=unknown
 */

// ── Colors ──────────────────────────────────────────────
export const colors = {
  // Primary brand
  primary: "#57837E",
  primaryDark: "#4a6f6b",
  primaryLight: "#E8EFEE",
  primarySubtle: "#F0F4F3",

  // Text (3 levels)
  textPrimary: "#1E293B",
  textSecondary: "#64748B",
  textMuted: "#94A3B8",

  // Backgrounds (3 levels)
  white: "#FFFFFF",
  bg: "#F8FAFC",
  bgHover: "#F1F5F9",

  // Borders (2 levels)
  border: "#E2E8F0",
  borderHover: "#CBD5E1",

  // Status — success
  success: "#10B981",
  successBg: "#DCFCE7",
  successText: "#166534",

  // Status — error
  error: "#EF4444",
  errorBg: "#FEE2E2",
  errorText: "#DC2626",

  // Status — warning
  warning: "#F59E0B",
  warningBg: "#FEF3C7",
  warningText: "#D97706",

  // Status — info (also used for male gender)
  info: "#3B82F6",
  infoBg: "#EFF6FF",
  infoText: "#1D4ED8",

  // Neutral (chips, unknown gender)
  neutral: "#6B7280",
  neutralBg: "#F3F4F6",

  // Female gender
  female: "#BE185D",
  femaleBg: "#FDF2F8",

  // Extra tokens (used by ActionMenu, ConfirmDialog)
  dark: "#1E293B",
  danger: "#F16063",
};

// ── Avatar palette (subset of the palette above) ────────
// Used for user avatars, chart colors, accent variations.
// Every color here comes from the palette above.
export const avatarPalette = [
  colors.primary,     // teal
  colors.info,        // blue
  colors.female,      // pink
  colors.warning,     // amber
  colors.success,     // green
  colors.error,       // red
  colors.neutral,     // gray
];

// ── Typography ──────────────────────────────────────────
export const typography = {
  fontFamily: "'Assistant', -apple-system, BlinkMacSystemFont, 'Roboto', sans-serif",

  h1: { fontSize: "28px", fontWeight: 700, lineHeight: "38px" },
  h2: { fontSize: "22px", fontWeight: 700, lineHeight: "30px" },
  h3: { fontSize: "18px", fontWeight: 600, lineHeight: "26px" },

  body: { fontSize: "14px", fontWeight: 400, lineHeight: "20px" },
  bodyMedium: { fontSize: "14px", fontWeight: 500, lineHeight: "20px" },
  bodySemibold: { fontSize: "14px", fontWeight: 600, lineHeight: "20px" },

  small: { fontSize: "13px", fontWeight: 400, lineHeight: "18px" },
  smallMedium: { fontSize: "13px", fontWeight: 500, lineHeight: "18px" },
  smallSemibold: { fontSize: "13px", fontWeight: 600, lineHeight: "18px" },

  tiny: { fontSize: "12px", fontWeight: 500, lineHeight: "16px" },

  stat: { fontSize: "20px", fontWeight: 700, lineHeight: "28px" },

  caption: { fontSize: "11px", fontWeight: 500, lineHeight: "16px" },
  label: { fontSize: "13px", fontWeight: 500, lineHeight: "18px" },
};

// ── Shadows ─────────────────────────────────────────────
export const shadows = {
  none: "none",
  xs: "0 1px 2px rgba(0, 0, 0, 0.05)",
  sm: "0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)",
  md: "0 4px 6px -1px rgba(0, 0, 0, 0.08), 0 2px 4px -1px rgba(0, 0, 0, 0.04)",
  lg: "0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.03)",
  card: "0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)",
  cardHover: "0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)",
  primary: "0 4px 14px rgba(87, 131, 126, 0.15)",
  dropdown: "0 8px 24px rgba(0, 0, 0, 0.12)",
};

// ── Borders ─────────────────────────────────────────────
export const borders = {
  radius: {
    sm: "6px",
    md: "8px",
    lg: "12px",
    xl: "16px",
    full: "9999px",
  },
  default: `1px solid ${colors.border}`,
  hover: `1px solid ${colors.borderHover}`,
  focus: `2px solid ${colors.primary}`,
};

// ── Spacing helpers ─────────────────────────────────────
export const spacing = {
  page: { xs: "12px", md: "20px" },
  card: { xs: "12px 14px", md: "16px 20px" },
  section: { xs: "12px", md: "16px" },
};

// Default export for backward compatibility
export default colors;
